/**
 * Modal player for a tutorial video.
 */
import * as React from 'react';
import { AlertCircle, Play } from 'lucide-react';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Spinner } from '@/components/ui/spinner';
import type { TutorialVideo } from '@/types/tutorial.types';
import { getErrorMessage } from '@/utils/errors-utils';
import { tutorialPlaybackUrls } from './tutorial-grid-utils';

/** Player dialog for one selected tutorial. */
interface TutorialVideoModalProps {
  video: TutorialVideo | null;
  open: boolean;
  onClose: () => void;
}

/** Modal player: playback URL, loading overlay, and a fallback when the file is missing. */
export const TutorialVideoModal: React.FC<TutorialVideoModalProps> = ({ video, open, onClose }) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  const [candidateIndex, setCandidateIndex] = React.useState(0);
  const failedIndex = React.useRef(-1);
  const videoUrls = video ? tutorialPlaybackUrls(video) : [];
  const activeUrl = videoUrls[candidateIndex];

  /** Reset spinner, error, and spelling index when a different video opens. */
  React.useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setCandidateIndex(0);
    failedIndex.current = -1;
  }, [open, video?.id]);

  /** Keep the spinner up and try the next accent spelling. Show the error after the last one. */
  const handleVideoError = () => {
    if (failedIndex.current === candidateIndex) return;
    failedIndex.current = candidateIndex;
    if (candidateIndex + 1 < videoUrls.length) {
      setCandidateIndex(candidateIndex + 1);
      setIsLoading(true);
      return;
    }
    setIsLoading(false);
    setHasError(true);
  };

  /** Drop the spinner once the current spelling can play. */
  const handleCanPlay = () => {
    setIsLoading(false);
    setHasError(false);
  };

  return (
    <BaseModal
      open={open && !!video}
      onClose={onClose}
      size="2xl"
      compactPadding
      hideContentWrapper
      contentOverflowVisible
      className="overflow-hidden !max-h-none"
      header={{
        icon: Play,
        title: video?.name ?? 'Tutoriel',
        titleSize: 'md',
      }}
    >
      <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-muted">
        {!hasError && isLoading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-muted">
            <Spinner size="lg" label="Chargement de la vidéo…" />
          </div>
        )}

        {hasError && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-background p-6 text-center">
            <AlertCircle
              className="mb-2 h-12 w-12"
              style={{ color: 'rgb(var(--color-error))' }}
              aria-hidden
            />
            <p className="text-sm font-medium" style={{ color: 'rgb(var(--color-error))' }}>
              {getErrorMessage('loadVideoFailed')}
            </p>
            <p className="max-w-sm text-xs text-muted-foreground">
              {getErrorMessage('videoInaccessible')}
            </p>
          </div>
        )}

        {video && open && !hasError && activeUrl && (
          <video
            key={`${video.id}-${candidateIndex}`}
            className={`h-full w-full bg-muted transition-opacity duration-200 ${
              isLoading ? 'opacity-0' : 'opacity-100'
            }`}
            controls
            autoPlay
            onCanPlay={handleCanPlay}
            onError={handleVideoError}
          >
            <source src={activeUrl} type="video/mp4" />
            <track kind="captions" srcLang="fr" label="Français" src="data:text/vtt,WEBVTT" />
          </video>
        )}
      </div>
    </BaseModal>
  );
};
